import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
} from '@mui/material';
import { companyService } from '../../../services/companyService';
import type { Company } from '../../../services/companyService';

interface CreateCompanyProps {
  open: boolean;
  onClose: () => void;
  onCompanyCreated: (company: Company) => void;
}

const CreateCompany: React.FC<CreateCompanyProps> = ({
  open,
  onClose,
  onCompanyCreated,
}) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Назва компанії обов\'язкова');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newCompany = await companyService.create({ name: name.trim() });
      onCompanyCreated(newCompany);
      setName('');
      onClose();
    } catch (err) {
      setError('Не вдалося створити компанію. Спробуйте ще раз.');
      console.error('Error creating company:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        }
      }}
    >
      <DialogTitle>Створити нову компанію</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            inputRef={inputRef}
            label="Назва компанії"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !name.trim()}
          >
            {loading ? 'Створення...' : 'Створити компанію'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCompany;
